import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../../components/ui/button';

export function RecordFilterPopover({ title, count, draftCount, children, onReset, onApply, disabled, error }: {
  title: string; count: number; draftCount: number; children: ReactNode; onReset: () => void;
  onApply: () => void; disabled: boolean; error?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null), trigger = useRef<HTMLButtonElement>(null), panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = () => { setOpen(false); trigger.current?.focus(); };
  useLayoutEffect(() => {
    if (!open || !panel.current || !trigger.current) return;
    const popup = panel.current;
    const position = () => {
      const rect = trigger.current!.getBoundingClientRect();
      const width = Math.min(360, window.innerWidth - 24);
      const below = window.innerHeight - rect.bottom - 12, above = rect.top - 12;
      const useBelow = below >= Math.min(420, above);
      Object.assign(popup.style, { width: `${width}px`, maxHeight: `${Math.max(120, Math.min(600, useBelow ? below : above))}px`,
        left: `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`,
        top: useBelow ? `${rect.bottom + 6}px` : 'auto', bottom: useBelow ? 'auto' : `${window.innerHeight - rect.top + 6}px` });
    };
    position(); popup.focus({ preventScroll: true });
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    window.addEventListener('pointerdown', outside); window.addEventListener('resize', position); window.addEventListener('scroll', position, true);
    return () => { window.removeEventListener('pointerdown', outside); window.removeEventListener('resize', position); window.removeEventListener('scroll', position, true); };
  }, [open]);
  return <div className="record-additional-filters" ref={root} onBlur={event => {
    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }} onKeyDown={event => { if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); event.stopPropagation(); close(); } }}>
    <Button ref={trigger} type="button" variant="outline" className={count || open ? 'record-filters-active' : undefined}
      aria-haspopup="dialog" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
      <SlidersHorizontal size={16} />Filters{count > 0 ? ` · ${count}` : ''}
    </Button>
    {open && <div ref={panel} id={id} role="dialog" aria-label={title} tabIndex={-1} className="record-filter-popover">
      <div className="record-filter-popover-header"><strong>{title}</strong><Button type="button" variant="ghost" size="icon" aria-label="Close additional filters" onClick={close}><X size={16} /></Button></div>
      <div className="record-filter-popover-body">{children}{error && <p role="alert">{error}</p>}</div>
      <div className="record-filter-popover-footer"><Button type="button" variant="outline" size="sm" onClick={onReset}>Reset</Button>
        <span className="small muted">{draftCount} active</span><Button type="button" size="sm" disabled={disabled} onClick={() => { onApply(); close(); }}>Apply filters</Button>
      </div>
    </div>}
  </div>;
}
